import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Loader2, CalendarClock } from "lucide-react";

const GOLD = "#C9A84C";
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtT = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export function AdminBillDeskReminders({ api }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/billdesk/reminders").then((r) => setData(r.data)).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/billdesk/reminders/run-now"); toast.success(`Reminder sweep done · ${r.data.reminded.length} reminded · ${r.data.expired.length} expired notices`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Sweep failed"); }
    finally { setBusy(false); }
  };
  if (!data) return null;
  return (
    <div className="border border-white/10 bg-black/30 p-4" data-testid="bd-reminders">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="font-grotesk text-[8px] uppercase tracking-[0.3em]" style={{ color: GOLD }}><BellRing size={11} className="inline mr-1 -mt-0.5" /> Subscription expiry reminders · {data.schedule} · {data.windows.join(" / ")} days before + expired notice</div>
        <button data-testid="bd-reminders-run" onClick={run} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 font-grotesk text-[8px] uppercase tracking-[0.24em] font-bold disabled:opacity-50" style={{ background: GOLD, color: "#000" }}>{busy ? <Loader2 size={11} className="animate-spin" /> : <BellRing size={11} />} Run sweep now</button>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div>
          <div className="font-grotesk text-[8px] uppercase tracking-[0.24em] text-[#8A8A82] mb-2">Active annual subscriptions · {data.subscriptions.length}</div>
          {data.subscriptions.length === 0 ? <div className="font-body text-xs text-[#8A8A82]">No annual subscriptions yet.</div> : (
            <ul className="space-y-1.5">{data.subscriptions.map((s) => {
              const d = s.entitlement.subscription_days_left; const sent = Object.keys(s.expiry_reminders);
              return (
                <li key={s.user_id} data-testid={`bd-sub-${s.user_id}`} className="flex flex-wrap items-center gap-2 border border-white/5 px-2.5 py-1.5 text-[11px]">
                  <CalendarClock size={11} style={{ color: d < 0 ? "#E07070" : d <= 7 ? "#E0A070" : GOLD }} />
                  <span className="text-[#F5F5F0] font-mono">{s.email}</span>
                  <span className="text-[#8A8A82]">until {fmt(s.subscription_until)}</span>
                  <span className="ml-auto font-mono" style={{ color: d < 0 ? "#E07070" : d <= 7 ? "#E0A070" : GOLD }}>{d < 0 ? "EXPIRED" : `${d}d left`}</span>
                  <span className="font-grotesk text-[8px] uppercase tracking-[0.18em] text-[#8A8A82]">{sent.length ? `sent: ${sent.join(", ")}` : "none sent"}</span>
                </li>);
            })}</ul>
          )}
        </div>
        <div>
          <div className="font-grotesk text-[8px] uppercase tracking-[0.24em] text-[#8A8A82] mb-2">Sweeps with activity · last run {fmtT(data.last_run_at)}</div>
          {data.runs.length === 0 ? <div className="font-body text-xs text-[#8A8A82]">No reminders sent yet.</div> : (
            <ul className="space-y-1 font-mono text-[10px]">{data.runs.map((r, i) => <li key={i} data-testid={`bd-run-${i}`} className="flex justify-between border border-white/5 px-2.5 py-1"><span className="text-[#C9C9C2]">{fmtT(r.run_at)} · {r.trigger}</span><span style={{ color: GOLD }}>{r.counts?.reminded || 0} reminded · {r.counts?.expired || 0} expired</span></li>)}</ul>
          )}
        </div>
      </div>
    </div>
  );
}
